// filepath: apps/web/src/features/directory/people-directory/PeopleDirectoryPage.tsx
//
// FR-UI-DIR-1 AC-1 — people directory master-list page.
//
// Owns the filter state (text search + status + unit), delegates data
// fetching to usePeopleList(), and renders one of six states per
// FR-UI-CORE-10: loading, permission-denied, validation-error,
// fetch-failure, rate-limit, success.
//
// INV-1: the cache key (built inside usePeopleList) includes
// tenantSlug as the second segment.
// INV-2: only server-emitted fields are surfaced in the row -- no PII
// reconstruction.

import { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { usePeopleList } from "../queries";
import {
  emptyPeopleFilter,
  type PeopleFilterState,
  type Person,
} from "../types";
import { PeopleDirectoryRow } from "./PeopleDirectoryRow";

export function PeopleDirectoryPage(): React.ReactElement {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<PeopleFilterState>(emptyPeopleFilter());
  const { data, state, error } = usePeopleList(filter);

  switch (state) {
    case "loading":
      return (
        <p className="directory-people__loading" data-testid="people-loading">
          {t("directory.people.state.loading")}
        </p>
      );
    case "permission-denied":
      return (
        <p
          className="directory-people__permission-denied"
          role="alert"
          data-testid="people-permission-denied"
        >
          {t("directory.people.state.permission_denied")}
        </p>
      );
    case "validation-error":
      return (
        <p
          className="directory-people__validation-error"
          role="alert"
          data-testid="people-validation-error"
        >
          {error?.message ?? t("directory.people.state.validation_error")}
        </p>
      );
    case "fetch-failure":
      return (
        <p
          className="directory-people__fetch-failure"
          role="alert"
          data-testid="people-fetch-failure"
        >
          {t("directory.people.state.fetch_failure")}
        </p>
      );
    case "rate-limit":
      return (
        <p
          className="directory-people__rate-limit"
          role="alert"
          data-testid="people-rate-limit"
        >
          {t("directory.people.state.rate_limit")}
        </p>
      );
    case "success": {
      const items = data?.items ?? [];
      return (
        <section className="directory-people" data-testid="people-page">
          <header className="directory-people__header">
            <h1>{t("directory.people.title", "People")}</h1>
            <input
              type="search"
              className="directory-people__search"
              data-testid="people-search-input"
              placeholder={t(
                "directory.people.search.placeholder",
                "Search by name…",
              )}
              value={filter.query}
              onChange={(e) =>
                setFilter({ ...filter, query: e.currentTarget.value })
              }
              aria-label={t(
                "directory.people.search.placeholder",
                "Search by name…",
              )}
            />
          </header>
          {items.length === 0 ? (
            <p className="directory-people__empty" data-testid="people-empty">
              {t("directory.people.empty", "No people match the filter.")}
            </p>
          ) : (
            <table className="directory-people__table">
              <thead>
                <tr>
                  <th scope="col">
                    {t("directory.people.column.name", "Name")}
                  </th>
                  <th scope="col">
                    {t("directory.people.column.title", "Title")}
                  </th>
                  <th scope="col">
                    {t("directory.people.column.unit", "Unit")}
                  </th>
                  <th scope="col">
                    {t("directory.people.column.status", "Status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((person: Person) => (
                  <PeopleDirectoryRow key={person.id} person={person} />
                ))}
              </tbody>
            </table>
          )}
        </section>
      );
    }
  }
}
